import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { byPrefixAndName } from '@awesome.me/kit-f089350b06/icons';
import { Tooltip } from 'react-tooltip';

// React-shared.
import StatusBadge from '@exonet/react-shared/lib/Elements/StatusBadge';

// App components.
import Input from 'app/client/components/Input';

// Styled.
import * as S from './styled';

const starIcon = byPrefixAndName.fas.star;
const trashIcon = byPrefixAndName.fas['trash-can'];
const plusIcon = byPrefixAndName.fas.plus;

const ContactList = ({
  form,
  fieldName,
  label,
  placeholder,
  type,
  primaryMode = 'none',
  addLabel,
  primaryLabel,
  setPrimaryLabel,
  badges = {},
}) => {
  const { control, getValues } = form;
  const { fields, append, remove, update } = useFieldArray({ control, name: fieldName });

  const showStars = primaryMode !== 'none' && (primaryMode === 'optional' || fields.length > 1);

  const setPrimary = useCallback(
    (index) => {
      const current = getValues(fieldName);
      current.forEach((entry, i) => {
        if (primaryMode === 'optional' && i === index && entry.isPrimary) {
          update(i, { ...entry, isPrimary: false });
          return;
        }
        update(i, { ...entry, isPrimary: i === index });
      });
    },
    [getValues, fieldName, update, primaryMode],
  );

  const handleRemove = useCallback(
    (index) => {
      const current = getValues(fieldName);
      const wasPrimary = current[index].isPrimary;
      remove(index);

      if (primaryMode === 'auto' && wasPrimary && current.length > 1) {
        const newIndex = index === 0 ? 0 : index - 1;
        const remaining = current.filter((_, i) => i !== index);
        update(newIndex, { ...remaining[newIndex], isPrimary: true });
      }
    },
    [getValues, fieldName, remove, update, primaryMode],
  );

  const handleAppend = useCallback(() => {
    if (primaryMode === 'auto' && fields.length === 1) {
      const current = getValues(fieldName);
      update(0, { ...current[0], isPrimary: true });
    }
    append({ value: '', isPrimary: false });
  }, [fields.length, getValues, fieldName, append, update, primaryMode]);

  return (
    <S.List>
      <S.ListLabel>{label}</S.ListLabel>
      {fields.map((field, index) => (
        <S.ContactRow key={field.id}>
          <Input
            form={form}
            name={`${fieldName}.${index}.value`}
            label={false}
            placeholder={placeholder}
            type={type}
          />
          {badges[field.value] && (
            <StatusBadge pill variant="warning" title={badges[field.value].title}>
              {badges[field.value].label}
            </StatusBadge>
          )}
          {showStars && (
            <>
              <span
                data-tooltip-id={`${fieldName}-primary-${index}`}
                data-tooltip-content={field.isPrimary ? primaryLabel : setPrimaryLabel}
              >
                <S.PrimaryRadio
                  type="button"
                  $active={field.isPrimary}
                  onClick={() => setPrimary(index)}
                >
                  <FontAwesomeIcon icon={starIcon} />
                </S.PrimaryRadio>
              </span>
              <Tooltip
                id={`${fieldName}-primary-${index}`}
                positionStrategy="fixed"
                style={{ zIndex: 300 }}
              />
            </>
          )}
          <S.RemoveButton
            type="button"
            onClick={() => handleRemove(index)}
            disabled={fields.length <= 1}
          >
            <FontAwesomeIcon icon={trashIcon} />
          </S.RemoveButton>
        </S.ContactRow>
      ))}
      <S.AddButton type="button" onClick={handleAppend}>
        <FontAwesomeIcon icon={plusIcon} />
        {addLabel}
      </S.AddButton>
    </S.List>
  );
};

ContactList.propTypes = {
  form: PropTypes.object.isRequired,
  fieldName: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  type: PropTypes.string,
  primaryMode: PropTypes.oneOf(['optional', 'auto', 'none']),
  addLabel: PropTypes.string.isRequired,
  primaryLabel: PropTypes.string,
  setPrimaryLabel: PropTypes.string,
  // A badge next to a row, by the row's initial text: `{ [value]: { label, title } }`.
  badges: PropTypes.objectOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, title: PropTypes.string }),
  ),
};

export default ContactList;
